// src/utils/aiSummary.js

// --- 1) Classificatore semplice dei formati TV (IT/EN keywords) ---
const GENRE_KEYWORDS = [
  { genre: 'talent di cucina', kw: ['masterchef', 'cook', 'cucina', 'chef', 'bake off', 'hell\'s kitchen'] },
  { genre: 'reality', kw: ['grande fratello', 'big brother', 'isola dei famosi', 'temptation', 'reality'] },
  { genre: 'docu-reality', kw: ['casa a prima vista', 'v vite', 'case', 'docu-reality', 'docu reality'] },
  { genre: 'game show', kw: ['cash or trash', 'affari tuoi', 'game show', 'quiz', 'guess', 'indovina'] },
  { genre: 'talent musicale', kw: ['x factor', 'the voice', 'talent musicale', 'music talent'] },
  { genre: 'factual', kw: ['factual', 'vite', 'vite al limite', 'case', 'cambio moglie'] },
  { genre: 'travel/adventure', kw: ['pechino express', 'indie', 'viaggio', 'travel', 'adventure'] },
  { genre: 'makeover', kw: ['makeover', 'restyling', 'cambio look', 'casa', 'trucco'] },
  { genre: 'talk show', kw: ['talk', 'chiacchiere', 'intervista', 'live talk', 'talk show'] },
  { genre: 'cooking factual', kw: ['destination gusto', 'food', 'ricette'] },
  { genre: 'dating', kw: ['appuntamento', 'dating', 'love', 'matrimonio'] },
  { genre: 'sport', kw: ['sport', 'calcio', 'basket', 'moto', 'motori'] },
];

export function classifyShowType(title = '') {
  const t = String(title).toLowerCase();
  for (const rule of GENRE_KEYWORDS) {
    if (rule.kw.some(k => t.includes(k))) return rule.genre;
  }
  return null; // unknown
}

// --- 2) Estrai info chiave da produzioni ---
export function extractProductionSignals(productions = []) {
  const counts = new Map();
  const titles = [];

  for (const p of productions) {
    const title = p?.title || p?.produzione || p?.programma || p?.nome || '';
    if (!title) continue;
    titles.push(title);

    const g = classifyShowType(title);
    if (g) counts.set(g, (counts.get(g) || 0) + 1);
  }

  // ordina per frequenza
  const top = [...counts.entries()].sort((a,b) => b[1]-a[1]).slice(0, 3).map(([g]) => g);
  return { topGenres: top, titles };
}

// --- 3) Normalizza skill in forma leggibile (usa già i tuoi normalizeSkills se vuoi) ---
function normalizeSkillLabels(skills = []) {
  // capitalizza parole base e rimuovi duplicati
  const seen = new Set();
  const norm = [];
  for (const raw of skills) {
    const s = String(raw).trim();
    if (!s) continue;
    const pretty = s
      .replace(/\s+/g,' ')
      .replace(/\b\w/g, c => c.toUpperCase()); // semplice Capitalize
    if (!seen.has(pretty.toLowerCase())) {
      seen.add(pretty.toLowerCase());
      norm.push(pretty);
    }
  }
  return norm;
}

// --- 4) Costruisci un riassunto "umano" (2-3 frasi) ---
export function buildAISummary({ bio, skills, productions, mainRole, seniority }) {
  const bioClean = (bio || '').trim();
  const skillsClean = normalizeSkillLabels(skills || []);
  const { topGenres } = extractProductionSignals(productions || []);

  // 1) headline
  let headline = 'Professionista di post-produzione';
  if (mainRole) {
    headline = `${mainRole}${seniority ? ' ' + seniority : ''}`.trim();
  }

  // 2) frasi dinamiche
  const parts = [];

  // stile e specializzazioni (da bio + skills)
  if (bioClean) {
    parts.push('profilo con approccio professionale e orientato ai risultati.');
  } else {
    parts.push('profilo solido e orientato alle consegne broadcast.');
  }

  if (skillsClean.length) {
    const core = skillsClean.slice(0, 3).join(', ');
    parts.push(`competenze chiave: ${core}.`);
  }

  if (topGenres.length) {
    parts.push(`esperienza su ${topGenres.join(', ')}.`);
  }

  // 3) Ricomponi con tono neutro
  // Evita ripetizioni banali: mescola in 2-3 frasi
  const sentence1 = `${headline} con ${bioClean ? 'buona' : 'solida'} esperienza in contesti televisivi e digital.`;
  const sentence2 = parts.slice(0,2).join(' ');
  const sentence3 = (parts[2] ? parts[2] : '').trim();

  // --- 5) Ricomposizione finale corretta ---
  let summary = [sentence1, sentence2, sentence3]
    .filter(Boolean)
    .map(s => s.trim().replace(/[.]+$/, '.')) // rimuove doppi punti
    .join(' '); // usa spazio singolo invece di aggiungere altri punti

  // normalizza spazi e capitalizza dopo punto/esclamativo/interrogativo
  summary = summary
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/([.!?])\s*([a-zà-öø-ÿ])/g, (_, sep, letter) => `${sep} ${letter.toUpperCase()}`);

  return summary;

}
